import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  Users, MapPin, Mail, Phone, Calendar, Check, Edit3, Trash2, Lock, ArrowRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useCampusAuth } from "@/contexts/CampusAuth";
import { formatApiErrorDetail } from "@/lib/apiError";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/talents`;

export default function CampusTalents() {
  const { user } = useCampusAuth();
  const isViewer = user?.tier === "Hauptquartier" || user?.is_admin;
  const [mine, setMine] = useState(null);
  const [list, setList] = useState([]);
  const [qualifications, setQualifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  // Treat an inactive (withdrawn) profile as "no profile" in the UI.
  const activeMine = mine && mine.is_active ? mine : null;

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const calls = [
          axios.get(`${API}/me`, { withCredentials: true }),
          axios.get(`${API}/qualifications`),
        ];
        if (isViewer) calls.push(axios.get(API, { withCredentials: true }));
        const results = await Promise.all(calls);
        if (!active) return;
        setMine(results[0].data);
        setQualifications(results[1].data);
        if (isViewer) setList(results[2].data);
      } catch (e) {
        if (active) toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Konnte Absolventen-Pool nicht laden.");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [isViewer]);

  const reload = async () => {
    const meRes = await axios.get(`${API}/me`, { withCredentials: true });
    setMine(meRes.data);
    if (isViewer) {
      const lst = await axios.get(API, { withCredentials: true });
      setList(lst.data);
    }
  };

  return (
    <div className="space-y-12">
      <header className="flex items-start justify-between gap-6 flex-wrap">
        <div>
          <div className="text-[12px] uppercase tracking-[0.28em] font-medium text-sand-500 mb-3">
            {isViewer ? `Personal finden  ·  ${list.length} Profile` : "Profil für Jobsuche"}
          </div>
          <h1 className="font-display text-sand-800 text-3xl sm:text-[2.4rem] font-semibold leading-[1.1]">
            {isViewer ? "Pflegekräfte, die wechseln wollen." : "Wirst du gesucht?"}
          </h1>
          <p className="mt-4 text-sand-600 text-[15.5px] leading-[1.65] max-w-2xl">
            {isViewer
              ? "Frisch ausgebildete Pflegekräfte aus unserem Bildungszentrum — LG1, LG2, examinierte PFK, PDLs und Praxisanleiter. Direkter Kontakt, ohne Headhunter-Aufschlag."
              : "Lege ein kurzes Profil an, wenn du eine neue Stelle suchst. Pflegedienste & Heime aus dem Campus sehen es und können dich direkt kontaktieren. Du behältst deine Kontaktdaten in der Hand."}
          </p>
        </div>
        {!isViewer && (
          <Button
            onClick={() => setShowForm(true)}
            data-testid="talents-edit-cta"
            className="rounded-full bg-sand-800 hover:bg-sand-700 text-cream-50 h-11 px-5 text-[13.5px] font-medium"
          >
            <span className="inline-flex items-center gap-2">
              <Edit3 className="h-4 w-4" strokeWidth={1.8} />
              {activeMine ? "Profil bearbeiten" : "Profil anlegen"}
            </span>
          </Button>
        )}
      </header>

      {/* Eigenes Profil-Status (für nicht-Viewer) */}
      {!isViewer && activeMine && (
        <MyTalentCard
          talent={activeMine}
          onEdit={() => setShowForm(true)}
          onWithdraw={async () => {
            if (!window.confirm("Profil wirklich zurückziehen?")) return;
            try {
              await axios.delete(`${API}/me`, { withCredentials: true });
              toast.success("Profil zurückgezogen.");
              try { await reload(); } catch {
                // ignore: even if reload fails, optimistic withdraw
              }
            } catch (e) {
              toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Zurückziehen fehlgeschlagen.");
            }
          }}
        />
      )}
      {!isViewer && !activeMine && !loading && (
        <EmptySelf onClick={() => setShowForm(true)} />
      )}

      {/* Trägersicht: Liste aller Talents */}
      {isViewer && (
        <section>
          {loading ? (
            <div className="text-sand-500 text-[14px]">Lade Absolventen …</div>
          ) : list.length === 0 ? (
            <EmptyList />
          ) : (
            <div className="grid lg:grid-cols-2 gap-5">
              {list.map((t) => <TalentCard key={t.id} talent={t} />)}
            </div>
          )}
        </section>
      )}

      {/* Footer hint for Basic-User */}
      {!isViewer && (
        <div className="rounded-2xl border border-cream-300 bg-cream-50 p-6 text-[13px] text-sand-600">
          <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-semibold text-terra mb-2">
            <Lock className="h-3.5 w-3.5" strokeWidth={1.8} />
            Sicherheit
          </div>
          Die Liste aller suchenden Pflegekräfte sehen ausschließlich Einrichtungen mit
          <strong className="text-sand-800"> Helden-Hauptquartier-Lizenz</strong>.
          Dein Profil wird erst angezeigt, wenn du es aktiv anlegst — und du kannst es jederzeit zurückziehen.
        </div>
      )}

      {showForm && (
        <TalentFormModal
          existing={mine}
          qualifications={qualifications}
          defaultEmail={user.email}
          onClose={() => setShowForm(false)}
          onSaved={async () => { setShowForm(false); await reload(); }}
        />
      )}
    </div>
  );
}

function MyTalentCard({ talent, onEdit, onWithdraw }) {
  return (
    <article className="rounded-2xl border border-terra/30 bg-peach-50 p-6 md:p-7" data-testid="talents-my-card">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-terra">
            Dein Profil · aktiv für Träger sichtbar
          </div>
          <h3 className="mt-2 font-display text-sand-800 text-[1.4rem] font-semibold leading-tight">{talent.headline}</h3>
          <div className="mt-2 text-[13px] text-sand-700">{talent.qualification} · {talent.region}</div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={onEdit}
            data-testid="talents-edit"
            className="inline-flex items-center gap-2 rounded-full border border-cream-300 bg-white hover:bg-cream-100 px-4 h-10 text-[12.5px] font-medium text-sand-700"
          >
            <Edit3 className="h-3.5 w-3.5" strokeWidth={1.7} /> Bearbeiten
          </button>
          <button
            onClick={onWithdraw}
            data-testid="talents-withdraw"
            className="inline-flex items-center gap-2 rounded-full border border-cream-300 bg-white hover:bg-cream-100 px-4 h-10 text-[12.5px] font-medium text-sand-700"
          >
            <Trash2 className="h-3.5 w-3.5" strokeWidth={1.7} /> Zurückziehen
          </button>
        </div>
      </div>
      <p className="mt-5 text-[14px] text-sand-700 leading-relaxed whitespace-pre-line">{talent.bio}</p>
    </article>
  );
}

function TalentCard({ talent }) {
  return (
    <article className="rounded-2xl border border-cream-300 bg-white p-6 flex flex-col" data-testid={`talent-card-${talent.id}`}>
      <div className="flex items-start gap-4">
        <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-terra/10 text-terra shrink-0">
          <Users className="h-5 w-5" strokeWidth={1.6} />
        </span>
        <div className="flex-1 min-w-0">
          <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-terra">{talent.qualification}</div>
          <h3 className="mt-1 font-display text-sand-800 text-[1.15rem] font-semibold leading-tight">{talent.headline}</h3>
          <div className="mt-2 flex items-center gap-3 text-[12.5px] text-sand-600 flex-wrap">
            <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" strokeWidth={1.7} />{talent.region}</span>
            {talent.open_to_relocation && (<><span>·</span><span>Umzug möglich</span></>)}
            {talent.earliest_start && (<><span>·</span><span className="inline-flex items-center gap-1"><Calendar className="h-3.5 w-3.5" strokeWidth={1.7} />ab {talent.earliest_start}</span></>)}
          </div>
        </div>
      </div>
      <p className="mt-4 text-[14px] text-sand-700 leading-relaxed whitespace-pre-line flex-1">{talent.bio}</p>
      <div className="mt-5 pt-5 border-t border-cream-200 flex items-center justify-between flex-wrap gap-3">
        <div className="text-[12px] text-sand-500">{talent.display_name}</div>
        <div className="flex items-center gap-3 text-[13px]">
          <a href={`mailto:${talent.contact_email}`} className="inline-flex items-center gap-1.5 text-terra hover:underline font-medium">
            <Mail className="h-3.5 w-3.5" strokeWidth={1.8} />Kontakt
          </a>
          {talent.contact_phone && (
            <a href={`tel:${talent.contact_phone}`} className="inline-flex items-center gap-1.5 text-sand-700 hover:text-sand-800">
              <Phone className="h-3.5 w-3.5" strokeWidth={1.8} />{talent.contact_phone}
            </a>
          )}
        </div>
      </div>
    </article>
  );
}

function EmptySelf({ onClick }) {
  return (
    <div className="rounded-3xl border border-dashed border-cream-300 bg-white p-10 text-center">
      <Users className="h-8 w-8 text-sand-400 mx-auto" strokeWidth={1.4} />
      <h3 className="mt-4 font-display text-sand-800 text-xl font-semibold">Noch kein Profil.</h3>
      <p className="mt-2 text-sand-600 text-[14px] max-w-md mx-auto">
        Lege in 2 Minuten ein kurzes Profil an. Du entscheidest, ob es sichtbar bleibt.
      </p>
      <Button onClick={onClick} className="mt-6 rounded-full bg-sand-800 hover:bg-sand-700 text-cream-50 h-11 px-5">
        <span className="inline-flex items-center gap-2"><Edit3 className="h-4 w-4" strokeWidth={1.8} />Profil anlegen</span>
      </Button>
    </div>
  );
}

function EmptyList() {
  return (
    <div className="rounded-3xl border border-dashed border-cream-300 bg-white p-12 text-center">
      <Users className="h-8 w-8 text-sand-400 mx-auto" strokeWidth={1.4} />
      <h3 className="mt-4 font-display text-sand-800 text-xl font-semibold">Noch keine Absolventen aktiv.</h3>
      <p className="mt-2 text-sand-600 text-[14px] max-w-md mx-auto">
        Sobald Kursteilnehmer und Pflegekräfte ihr Profil aktivieren, erscheinen sie hier.
      </p>
    </div>
  );
}

function TalentFormModal({ existing, qualifications, defaultEmail, onClose, onSaved }) {
  const [form, setForm] = useState(() => existing || {
    qualification: qualifications[0] || "",
    headline: "",
    bio: "",
    region: "",
    earliest_start: "",
    contact_email: defaultEmail || "",
    contact_phone: "",
    open_to_relocation: false,
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    setBusy(true);
    try {
      await axios.put(API + "/me", form, { withCredentials: true });
      toast.success("Profil gespeichert.");
      await onSaved();
    } catch (e2) {
      setErr(formatApiErrorDetail(e2?.response?.data?.detail) || "Speichern fehlgeschlagen.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-sand-800/30 backdrop-blur-sm p-4">
      <div className="relative w-full max-w-2xl rounded-2xl bg-white border border-cream-300 shadow-hover p-7 md:p-8 overflow-y-auto max-h-[90vh]">
        <h2 className="font-display text-sand-800 text-2xl font-semibold">
          {existing ? "Profil bearbeiten" : "Neues Profil"}
        </h2>
        <p className="mt-1 text-sand-600 text-[13.5px]">Sichtbar für Helden-Hauptquartier-Träger.</p>

        <form onSubmit={submit} className="mt-6 grid gap-4" data-testid="talent-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-[11px] uppercase tracking-[0.22em] font-semibold text-sand-500 mb-2 block">Qualifikation</label>
              <select
                value={form.qualification}
                onChange={(e) => setForm({ ...form, qualification: e.target.value })}
                data-testid="talent-form-qualification"
                className="w-full rounded-xl border border-cream-300 bg-white px-3 h-11 text-[14.5px] outline-none focus:border-sand-800"
              >
                {qualifications.map((q) => (<option key={q} value={q}>{q}</option>))}
              </select>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-[0.22em] font-semibold text-sand-500 mb-2 block">Region</label>
              <input
                type="text" required value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })}
                data-testid="talent-form-region"
                className="w-full rounded-xl border border-cream-300 bg-white px-4 h-11 text-[14.5px] outline-none focus:border-sand-800"
              />
            </div>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-[0.22em] font-semibold text-sand-500 mb-2 block">Headline</label>
            <input
              type="text" required minLength={4} maxLength={140} value={form.headline}
              onChange={(e) => setForm({ ...form, headline: e.target.value })}
              data-testid="talent-form-headline"
              placeholder="z. B. Examinierte Pflegefachkraft, sucht ruhige Schichten im Norden"
              className="w-full rounded-xl border border-cream-300 bg-white px-4 h-11 text-[14.5px] outline-none focus:border-sand-800"
            />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-[0.22em] font-semibold text-sand-500 mb-2 block">Über dich (mind. 20 Zeichen)</label>
            <textarea
              required minLength={20} maxLength={2000} rows={5} value={form.bio}
              onChange={(e) => setForm({ ...form, bio: e.target.value })}
              data-testid="talent-form-bio"
              className="w-full rounded-xl border border-cream-300 bg-white px-4 py-3 text-[14.5px] outline-none focus:border-sand-800 resize-y"
            />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-[11px] uppercase tracking-[0.22em] font-semibold text-sand-500 mb-2 block">Frühester Start (optional)</label>
              <input
                type="text" value={form.earliest_start || ""} onChange={(e) => setForm({ ...form, earliest_start: e.target.value })}
                data-testid="talent-form-start"
                placeholder="z. B. 01.04.2026"
                className="w-full rounded-xl border border-cream-300 bg-white px-4 h-11 text-[14.5px] outline-none focus:border-sand-800"
              />
            </div>
            <div className="flex items-end pb-1">
              <label className="inline-flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox" checked={!!form.open_to_relocation}
                  onChange={(e) => setForm({ ...form, open_to_relocation: e.target.checked })}
                  data-testid="talent-form-relocation"
                  className="h-4 w-4 accent-terra"
                />
                <span className="text-[14px] text-sand-700">Umzug möglich</span>
              </label>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-[11px] uppercase tracking-[0.22em] font-semibold text-sand-500 mb-2 block">Kontakt-E-Mail</label>
              <input
                type="email" required value={form.contact_email}
                onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
                data-testid="talent-form-email"
                className="w-full rounded-xl border border-cream-300 bg-white px-4 h-11 text-[14.5px] outline-none focus:border-sand-800"
              />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-[0.22em] font-semibold text-sand-500 mb-2 block">Telefon (optional)</label>
              <input
                type="text" value={form.contact_phone || ""} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })}
                data-testid="talent-form-phone"
                className="w-full rounded-xl border border-cream-300 bg-white px-4 h-11 text-[14.5px] outline-none focus:border-sand-800"
              />
            </div>
          </div>
          {err && <p className="text-[13px] text-terra font-medium" data-testid="talent-form-error">{err}</p>}
          <div className="flex items-center justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="text-[13.5px] text-sand-600 hover:text-sand-800 underline-offset-4 hover:underline">
              Abbrechen
            </button>
            <Button type="submit" disabled={busy} data-testid="talent-form-submit" className="rounded-full bg-sand-800 hover:bg-sand-700 text-cream-50 h-11 px-5 disabled:opacity-60">
              <span className="inline-flex items-center gap-2">{busy ? "Speichere…" : "Profil speichern"}{!busy && <Check className="h-4 w-4" strokeWidth={2} />}</span>
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
